import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type GestorSetor } from "../api";
import ErrorState from "./ErrorState";
import { compararNomes } from "../ordem";
import { IconRefresh, IconSearch, IconClose } from "../icons";

const SITUACOES = ["Conforme", "Não conformidade"];

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

const tomSituacao = (s: string | null) =>
  s === "Conforme" ? "ok" : s === "Não conformidade" ? "danger" : "neutral";

/**
 * Gestores por Setor.
 *
 * O cadastro NÃO é feito aqui: quem contrata é o RH. Ao registrar lá alguém com
 * cargo de gestor num departamento, a pessoa aparece nesta tela sozinha, com o
 * cadastro dela. O que é da Qualidade — a avaliação (Conforme / Não
 * conformidade) e o registro escrito — é lançado aqui, e cada lançamento vira
 * histórico, para dar para acompanhar a evolução do setor.
 */
export default function GestoresSetorView() {
  const [itens, setItens] = useState<GestorSetor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const [avaliando, setAvaliando] = useState<GestorSetor | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.gestores.list();
      setItens(r.itens ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const achados = !q
      ? itens
      : itens.filter((g) =>
          [g.nome, g.cargo, g.departamento, g.email, g.telefone].some((v) =>
            String(v ?? "").toLowerCase().includes(q),
          ),
        );
    // Ordem alfabética garantida na tela: setor A→Z (sem setor por último) e,
    // dentro do setor, o gestor A→Z.
    return [...achados].sort(
      (a, b) =>
        Number(!a.departamento) - Number(!b.departamento) ||
        compararNomes(a.departamento, b.departamento) ||
        compararNomes(a.nome, b.nome),
    );
  }, [itens, busca]);

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={carregar} />;

  return (
    <div className="crud">
      <div className="crud-aviso">
        O cadastro do gestor é feito no <strong>RH</strong>: quem for contratado com cargo de
        gestor (ou administrador, coordenador, supervisor, encarregado, gerente, diretor) em um
        departamento aparece aqui automaticamente, com nome, cargo, e-mail e telefone. Nesta tela a
        Qualidade registra a <strong>situação</strong> de cada um — Conforme ou Não conformidade —
        com as informações escritas.
      </div>

      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar por nome, cargo, setor, e-mail..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{filtrados.length} gestor(es)</span>
          <button className="btn-secondary" onClick={carregar} title="Buscar novamente no RH">
            <IconRefresh size={16} /> Atualizar
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando…</div>
        ) : filtrados.length === 0 ? (
          <div className="empty">
            {itens.length === 0
              ? "Nenhum gestor cadastrado no RH ainda. Cadastre a pessoa no RH com um cargo de gestor e o departamento — ela aparece aqui."
              : `Nenhum gestor encontrado para "${busca}".`}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Setor</th>
                <th>Gestor</th>
                <th>Cargo</th>
                <th>E-mail</th>
                <th>Telefone</th>
                <th>Situação</th>
                <th>Informações</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((g) => (
                <tr key={g.rh_id}>
                  <td>
                    <strong>{g.departamento || "—"}</strong>
                  </td>
                  <td>
                    {g.nome}
                    {g.status && g.status !== "Ativo" && (
                      <>
                        <br />
                        <span className="pill pill-warn">{g.status}</span>
                      </>
                    )}
                  </td>
                  <td>{g.cargo || "—"}</td>
                  <td>{g.email || "—"}</td>
                  <td>{g.telefone || "—"}</td>
                  <td>
                    {g.situacao ? (
                      <span className={`pill pill-${tomSituacao(g.situacao)}`}>{g.situacao}</span>
                    ) : (
                      <span className="pill pill-neutral">Sem avaliação</span>
                    )}
                  </td>
                  <td>
                    {g.observacao || "—"}
                    {g.avaliadoEm && (
                      <>
                        <br />
                        <small>
                          {dataBR(g.avaliadoEm)}
                          {g.avaliador ? ` · ${g.avaliador}` : ""}
                        </small>
                      </>
                    )}
                  </td>
                  <td className="col-actions">
                    <button className="btn-primary btn-sm" onClick={() => setAvaliando(g)}>
                      Avaliar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {avaliando && (
        <AvaliarModal
          gestor={avaliando}
          onClose={() => setAvaliando(null)}
          onSaved={() => {
            setAvaliando(null);
            void carregar();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------- Modal de avaliação ------------------------- */
function AvaliarModal({
  gestor,
  onClose,
  onSaved,
}: {
  gestor: GestorSetor;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [situacao, setSituacao] = useState(gestor.situacao ?? "Conforme");
  const [observacao, setObservacao] = useState("");
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function salvar() {
    if (situacao === "Não conformidade" && !observacao.trim()) {
      setErro("Descreva a não conformidade no campo de informações.");
      return;
    }
    setSaving(true);
    setErro(null);
    try {
      await api.gestores.avaliar({
        rh_id: gestor.rh_id,
        gestor: gestor.nome,
        departamento: gestor.departamento ?? "",
        situacao,
        observacao: observacao.trim(),
      });
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Falha ao registrar a avaliação.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            {gestor.nome} — {gestor.departamento || "sem setor"}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {/* Cadastro do gestor: vem do RH, aqui é só consulta. */}
          <div className="gestor-cadastro">
            <div>
              <span>Cargo</span>
              <strong>{gestor.cargo || "—"}</strong>
            </div>
            <div>
              <span>E-mail</span>
              <strong>{gestor.email || "—"}</strong>
            </div>
            <div>
              <span>Telefone</span>
              <strong>{gestor.telefone || "—"}</strong>
            </div>
            <div>
              <span>Situação no RH</span>
              <strong>{gestor.status || "—"}</strong>
            </div>
          </div>

          <div className="form-grid">
            <label className="field">
              <span>Situação</span>
              <select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                {SITUACOES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field-wide">
              <span>
                Informações
                {situacao === "Não conformidade" && <em className="req"> *</em>}
              </span>
              <textarea
                rows={4}
                value={observacao}
                placeholder={
                  situacao === "Não conformidade"
                    ? "Descreva a não conformidade encontrada no setor…"
                    : "Observações da avaliação (opcional)…"
                }
                onChange={(e) => setObservacao(e.target.value)}
              />
            </label>
          </div>

          {erro && <div className="form-error">{erro}</div>}

          {gestor.historico.length > 0 && (
            <>
              <h4 className="section-title">Avaliações anteriores</h4>
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Situação</th>
                    <th>Informações</th>
                    <th>Avaliador</th>
                  </tr>
                </thead>
                <tbody>
                  {gestor.historico.map((h) => (
                    <tr key={h.id}>
                      <td>{dataBR(h.data)}</td>
                      <td>
                        <span className={`pill pill-${tomSituacao(h.situacao)}`}>{h.situacao}</span>
                      </td>
                      <td>{h.observacao || "—"}</td>
                      <td>{h.avaliador || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={salvar} disabled={saving}>
            {saving ? "Registrando…" : "Registrar avaliação"}
          </button>
        </div>
      </div>
    </div>
  );
}
